import { ReactElement, useState } from 'react';

import * as CSS from 'csstype';
import styled from 'styled-components';

import { IconProps } from '@components/icons';
import { IconCloseRegular } from '@components/icons/close-regular';
import { Stack } from '@components/stack';
import { Font, theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { Text } from '../text';

export interface InfoBoxProps {
  variant?: 'blue' | 'red' | 'orange' | 'green' | 'indigo';
  text?: string;
  font?: Font;
  icon?: ReactElement<IconProps>;
  children?: ReactElement;
  header?: string;
  closable?: boolean;
  onClose?: () => void;
  align?: CSS.Property.AlignItems;
}

export function InfoBox(props: InfoBoxProps) {
  const {
    text,
    variant = 'blue',
    font = 'regular14',
    icon,
    children,
    header,
    closable,
    onClose,
    align,
  } = props;
  const [isVisible, setIsVisible] = useState(true);

  const handleClose = () => {
    setIsVisible(false);
    if (onClose) {
      onClose();
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <Container data-variant={variant}>
      <Stack space={2} orientation="horizontal" align={align}>
        {icon}
        <TextContainer $font={font}>
          {header && <Text font={font} text={header} />}
          {text ? <Text text={text} font={font} /> : children}
        </TextContainer>
        {closable && (
          <StyledCloseButtonContainer data-variant={variant}>
            <IconCloseRegular onClick={handleClose} />
          </StyledCloseButtonContainer>
        )}
      </Stack>
    </Container>
  );
}

// Icons render at the default 24px. Keeping the text column at least that tall and
// vertically centred means a single line lines up with the icon at any font size,
// without per-line-height margin tweaks.
const ICON_SIZE = 24;

const TextContainer = styled.div<{ $font: Font }>((p) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: pxToRem(ICON_SIZE),
  font: theme.font[p.$font],
}));

export const Container = styled.div({
  padding: pxToRem(12),
  borderRadius: pxToRem(4),
  '&[data-variant=blue]': {
    backgroundColor: theme.color.blueTrans5,
    '--icon-color': theme.color.elementBlue100,
    span: {
      color: theme.color.elementBlue100,
    },
  },
  '&[data-variant=red]': {
    backgroundColor: theme.color.redTrans10,
    '--icon-color': theme.color.elementRed200,
    span: {
      color: theme.color.elementRed200,
    },
  },
  '&[data-variant=orange]': {
    backgroundColor: theme.color.orangeTrans10,
    '--icon-color': theme.color.elementOrange200,
    span: {
      color: theme.color.elementOrange200,
    },
  },
  '&[data-variant=green]': {
    backgroundColor: theme.color.greenTrans5,
    '--icon-color': theme.color.elementGreen200,
    span: {
      color: theme.color.elementGreen200,
    },
  },
  '&[data-variant=indigo]': {
    backgroundColor: theme.color.indigoTrans5,
    '--icon-color': theme.color.elementIndigo100,
    span: {
      color: theme.color.elementIndigo100,
    },
  },
});

const StyledCloseButtonContainer = styled.div({
  cursor: 'pointer',
  height: '100%',
  display: 'flex',
  alignItems: 'center',
  marginLeft: 'auto',
  '&[data-variant="green"]': {
    '--icon-color': theme.color.elementGreen100,
    ':hover': {
      '--icon-color': theme.color.elementGreen200,
    },
  },
  '&[data-variant="blue"]': {
    '--icon-color': theme.color.elementBlue100,
    ':hover': {
      '--icon-color': theme.color.elementBlue200,
    },
  },
  '&[data-variant="indigo"]': {
    '--icon-color': theme.color.elementIndigo100,
    ':hover': {
      '--icon-color': theme.color.elementIndigo200,
    },
  },
  '&[data-variant="orange"]': {
    '--icon-color': theme.color.elementOrange100,
    ':hover': {
      '--icon-color': theme.color.elementOrange200,
    },
  },
  '&[data-variant="red"]': {
    '--icon-color': theme.color.elementRed100,
    ':hover': {
      '--icon-color': theme.color.elementRed200,
    },
  },
});
